import { getTodayInDefaultTimeZone } from '@utils/helpers'
import type { SingleParserBuilder } from 'nuqs'

import { cookieStorage } from './cookie-storage'
import { rejectPastDate } from './parsers/date.parser'

// biome-ignore lint/suspicious/noExplicitAny: nuqs parsers are heterogeneous by URL-state key and invariant in their value type (`serialize` is contravariant, `parse` covariant), so `unknown` would reject every concrete parser.
export type UrlStateParserMap = Record<string, SingleParserBuilder<any>>

// biome-ignore lint/suspicious/noExplicitAny: see UrlStateParserMap.
type UrlStateSchema<T> = Record<keyof T, SingleParserBuilder<any>>

export type UrlStateStorageOptions<T = Record<string, unknown>> = {
  adapter?: 'cookie'
  persist?: (keyof T)[]
  storagePrefix?: string
  defaultValue?: T
  initialUrlSync?: boolean | Array<keyof T>
  shouldUseStorageValue?: (key: string, searchParams: URLSearchParams) => boolean
}

type MergeStateWithStorageParams<T> = {
  defaultState?: T
  urlState: T
  schema: UrlStateSchema<T>
  storage: UrlStateStorageOptions<T>
  /**
   * `null` marks a render that must not touch client-only inputs: the server
   * render and the client's hydration render. See {@link mergeStateWithStorage}.
   */
  urlSearchParams: URLSearchParams | null
  preferDefaultForUrlKeys?: boolean
}

/**
 * Resolves the effective URL state for one render.
 *
 * With `urlSearchParams === null` the snapshot is a pure projection of
 * `defaultState` — the value the server resolved for this request (URL >
 * cookies > parser defaults, see `resolveRouteUrlState`). Both the server
 * render and the client's hydration render take this branch, so they cannot
 * disagree: neither reads `document.cookie` (client-only) nor the wall clock
 * (server request time vs. browser time, which drift apart across a day
 * boundary or on a skewed device). Any divergence here surfaces as a React
 * text hydration mismatch (minified error #418) in date and pax labels.
 *
 * With `urlSearchParams` set — every render after hydration — values resolve
 * per key with priority URL > storage > default:
 * 1. key present in the current URL query: use the nuqs value (or, before the
 *    provider is initialized, the server-resolved default, which nuqs may not
 *    reflect yet after a client-side navigation)
 * 2. key persisted and `shouldUseStorageValue` allows it: use the stored value
 *    and fall back to the default when it is absent or unparsable
 * 3. otherwise: use the default
 *
 * Past dates are rejected in this branch only, against the browser clock.
 */
export const mergeStateWithStorage = <T extends Record<string, unknown>>({
  defaultState,
  urlState,
  schema,
  storage,
  urlSearchParams,
  preferDefaultForUrlKeys = false,
}: MergeStateWithStorageParams<T>): T => {
  if (!urlSearchParams) return pickSchemaKeys(defaultState, schema, urlState)

  const { persist = [], shouldUseStorageValue } = storage
  const result = { ...urlState }
  const adapter = getAdapter(storage?.adapter)
  const today = getTodayInDefaultTimeZone()

  for (const key in schema) {
    if (!Object.hasOwn(schema, key)) continue
    const isInUrl = urlSearchParams.has(key)

    if (isInUrl) {
      result[key] = rejectPastDateValue(
        preferDefaultForUrlKeys ? getStateValue(defaultState, key) : urlState[key],
        today
      )
    }
    // If the key is persistent, read from storage and parse
    else if (persist.includes(key)) {
      const allowFromStorage = shouldUseStorageValue?.(key, urlSearchParams) ?? true

      if (allowFromStorage) {
        const stored = adapter.get(key)
        if (stored) {
          try {
            const parsedStored = rejectPastDateValue(schema[key].parse(stored), today)
            result[key] =
              parsedStored ?? rejectPastDateValue(getStateValue(defaultState, key), today)
            continue
          } catch (error) {
            console.warn(`Failed to parse stored value for key ${key}:`, error)
          }
        }
      }
      result[key] = rejectPastDateValue(getStateValue(defaultState, key), today)
    } else {
      result[key] = rejectPastDateValue(getStateValue(defaultState, key), today)
    }
  }

  return result
}

/**
 * Narrows a state object to the schema's keys without normalizing values, so
 * the shape matches the post-hydration snapshot.
 */
const pickSchemaKeys = <T extends Record<string, unknown>>(
  state: T | undefined,
  schema: UrlStateSchema<T>,
  fallback: T
): T => {
  const result = { ...fallback }

  for (const key in schema) {
    if (!Object.hasOwn(schema, key)) continue
    result[key] = getStateValue(state, key)
  }

  return result
}

function getStateValue<T extends Record<string, unknown>, K extends keyof T>(
  state: T | undefined,
  key: K
): T[K]
function getStateValue(state: Record<string, unknown> | undefined, key: string): unknown {
  return state?.[key]
}

function rejectPastDateValue<T>(value: T, today: Date): T
function rejectPastDateValue(value: unknown, today: Date): unknown {
  if (!(value instanceof Date)) return value

  return rejectPastDate(value, today)
}

export const rejectPastDatesInState = <T extends object>(state: T, today: Date): T => {
  const result = { ...state }

  for (const key in state) {
    if (!Object.hasOwn(state, key)) continue
    result[key] = rejectPastDateValue(state[key], today)
  }

  return result
}

const ADAPTERS = { cookie: cookieStorage }

export const getAdapter = (adapter: 'cookie' = 'cookie') => {
  return ADAPTERS[adapter]
}
